import { track } from './amplitude';
import { SESSION_SUMMARY } from './events';
import { sceneEntered } from './sceneTracker';
import { sceneLoadingFinished } from '../state/sceneLoading';
import { userSelectedSight } from '../state/appState';
import { assistantPromptSubmitted } from '../state/assistantHud';
import {
  historicalFeatureOpened,
  historicalSliderSlideChanged,
  eraSceneEntered,
} from './historicalScenesTracker';
import { isEraScene } from '../scene/eraScenes/registry';
import { MAIN_BANNER_SCENE_ID_MAP } from '../scene/sceneBannerMap';
import type { SightId } from '../types';

/**
 * Session Summary — один агрегат на всю сессию, эмит на `pagehide` (рядом с
 * `Session Exit`). Несёт СЫРЫЕ session-level агрегаты; Track 1 / Track 2 /
 * Completed Experience считаются в аналитике (BI) поверх этих и других
 * событий и НЕ зашиваются в клиент — пороги в `ANALYTICS_OVERVIEW.md`
 * помечены как гипотезы под рекалибровку, перекалибровка не должна требовать
 * релиза.
 *
 * Самодостаточный (модель — `cameraSessionTracker` / `performanceTracker`):
 * подписывается на те же effector-события, что уже эмитят существующие
 * трекеры, и ничего в них не меняет. Аккумулятор — module-level singleton,
 * по природе session-scoped (модуль грузится один раз на загрузку страницы).
 */

// 4 главные локации в URL-форме (`kaaba` / `arafat` / `mina` / `muzdalifah`).
// Деривим из канонической backend→URL карты — она по построению исключает
// `overall` (root-меню) и inside-сцены, поэтому `locations_visited_count`
// («локаций из 4») не завышается, даже если `KNOWN_SCENES` позже изменится.
export const MAIN_LOCATION_IDS = new Set<string>(
  Object.values(MAIN_BANNER_SCENE_ID_MAP),
);

type SessionAccumulator = {
  reachedInteractive: boolean;
  mainScenes: Set<string>;
  poiOpened: Set<SightId>;
  chatMessages: number;
  // Historical scenes — touched the feature + how widely it was browsed/entered.
  historicalClockTapped: boolean;
  historicalErasViewed: Set<string>;
  historicalErasEntered: Set<string>;
};

const accumulator: SessionAccumulator = {
  reachedInteractive: false,
  mainScenes: new Set<string>(),
  poiOpened: new Set<SightId>(),
  chatMessages: 0,
  historicalClockTapped: false,
  historicalErasViewed: new Set<string>(),
  historicalErasEntered: new Set<string>(),
};

export const startSessionSummaryTracking = (): void => {
  // «Дошёл до интерактива» — случилось ли хоть раз первое `Scene Fully Loaded`.
  sceneLoadingFinished.watch(() => {
    accumulator.reachedInteractive = true;
  });

  // Широта по локациям — distinct из 4 главных. `sceneEntered` летит ещё и на
  // inside-сцены и на `overall`, поэтому фильтруем явным списком главных.
  sceneEntered.watch((sceneId) => {
    if (MAIN_LOCATION_IDS.has(sceneId)) {
      accumulator.mainScenes.add(sceneId);
    }
  });

  // «Открыл POI» = намеренное действие юзера (= `POI Viewed`). Свой
  // session-wide Set, а не `is_first_view` (тот per-scene, сбрасывается на
  // смене сцены). Track 1 (глубина) считается отдельно из `Scene POI Visited`.
  userSelectedSight.watch(({ id }) => {
    accumulator.poiOpened.add(id);
  });

  // Сообщения в чат — те же сабмиты, что дают `Assistant Message Sent`.
  assistantPromptSubmitted.watch(() => {
    accumulator.chatMessages += 1;
  });

  // Исторические сцены — тронул ли фичу + насколько широко полистал/вошёл.
  // Те же effector-события, что эмитят `Historical Feature Opened` /
  // `Historical Slider Slide Changed` / `Era Scene Entered`. Виды считают
  // только ЭРЫ (карточка «today» — возврат, не эпоха); входы — state-level
  // `eraSceneEntered`, чтобы меню / deep-link / browser back-forward тоже
  // попадали в rollup (не только Explore из слайдера).
  historicalFeatureOpened.watch(() => {
    accumulator.historicalClockTapped = true;
  });
  historicalSliderSlideChanged.watch(({ toSceneId }) => {
    if (isEraScene(toSceneId)) accumulator.historicalErasViewed.add(toSceneId);
  });
  eraSceneEntered.watch(({ sceneId }) => {
    accumulator.historicalErasEntered.add(sceneId);
  });

  // Эмит ровно один раз на сессию. `pagehide` надёжнее `beforeunload` на
  // мобильных (см. `sessionTracker`); guard защищает от повторного вызова
  // (bfcache restore → повторный pagehide).
  let dispatched = false;
  window.addEventListener('pagehide', () => {
    if (dispatched) return;
    dispatched = true;
    track(SESSION_SUMMARY, {
      reached_interactive: accumulator.reachedInteractive,
      locations_visited_count: accumulator.mainScenes.size,
      total_poi_opened: accumulator.poiOpened.size,
      chat_messages_count: accumulator.chatMessages,
      historical_clock_tapped: accumulator.historicalClockTapped,
      historical_eras_viewed_count: accumulator.historicalErasViewed.size,
      // Заменило historical_eras_selected_count (июль 2026): считает входы в
      // эры ЛЮБЫМ путём (слайдер / меню / deep-link / back-forward), не только
      // Explore из слайдера.
      historical_eras_entered_count: accumulator.historicalErasEntered.size,
    });
  });
};
